import { useCallback, useEffect, useState } from "react";
import { Role, useSessionState } from "../../../../Session/SessionService";
import { boolean } from "zod";
import {
  Button,
  Checkbox,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MailIcon } from "lucide-react";

export interface EmailAndDiscountSubstepState {
  email: string | null;
  discount: string | null;
  askForSignout: boolean;
  emailVerificationNeeded: boolean;
  valid: boolean;
}

interface StateAndModifiers {
  state: EmailAndDiscountSubstepState;
  modifiers: {
    reset: () => void;
    setEmail: (newEmail: string) => void;
    setDiscount: (newDiscount: string | null) => void;
    doSignout: (shouldSignout: boolean) => Promise<void>;
  };
}

export function useSubstepGetEmailAndDiscount(
  paymentMethodRequiresAuthenticaiton: boolean
) {
  const session = useSessionState();

  const mustVerifyEmail = useCallback(() => {
    const mustVerify =
      paymentMethodRequiresAuthenticaiton && !session.hasAtLeastRole(Role.USER);
    //console.log("mustVerifyEmail ", mustVerify);
    return mustVerify;
  }, [paymentMethodRequiresAuthenticaiton, session.role]);

  const [state, setState] = useState<EmailAndDiscountSubstepState>({
    email: null,
    discount: null,
    askForSignout: session.hasAtLeastRole(Role.USER),
    valid: false,
    emailVerificationNeeded: mustVerifyEmail(),
  });

  const reset = useCallback(() => {
    // nothing...
  }, []);

  useEffect(() => {
    setState((old) => ({
      ...old,
      valid: isValidEmail(state.email),
      emailVerificationNeeded: mustVerifyEmail(),
    }));
  }, [state.email, mustVerifyEmail]);

  const setEmail = (newEmail: string) =>
    setState((old) => ({ ...old, email: newEmail }));

  const setDiscount = (newDiscount: string | null) =>
    setState((old) => ({ ...old, discount: newDiscount }));

  const doSignout = async (shouldSignout: boolean) => {
    if (shouldSignout) {
      return session.logout().then(() => {
        setState((old) => ({
          ...old,
          askForSignout: false,
          emailVerificationNeeded: paymentMethodRequiresAuthenticaiton,
        }));
      });
    } else {
      setState((old) => ({
        ...old,
        email: session.user,
        askForSignout: false,
      }));
    }
  };

  return {
    state: state,
    modifiers: { setEmail, setDiscount, doSignout, reset },
  } as StateAndModifiers;
}

export function EmailAndDiscountSelector(
  props: StateAndModifiers & {
    paymentTypeRequiresAuth: boolean;
    isInvoice: boolean;
  }
) {
  const session = useSessionState();
  const [verifySignout, setVerifySignout] = useState<boolean>(false);
  const [showDiscountField, setShowDiscountField] = useState<boolean>(
    props.state.discount != null
  );

  if (props.state.askForSignout) {
    return (
      <>
        {!verifySignout ? (
          <VStack>
            <Text>
              Välj att använda befintlig emailadress eller logga ut för att
              byta.
            </Text>{" "}
            <VStack>
              <Button
                colorScheme="green"
                onClick={() => props.modifiers.doSignout(false)}
              >
                {session.user}
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (props.paymentTypeRequiresAuth) {
                    setVerifySignout(true);
                  } else {
                    props.modifiers.doSignout(true);
                  }
                }}
              >
                Annan emailadress (Logga ut)
              </Button>
            </VStack>
          </VStack>
        ) : (
          <VStack>
            <Text fontStyle={"italic"}>
              Är du säker på att du vill logga ut? Du kommer behöva verifiera
              din emailadress i ett senare steg.
            </Text>
            <VStack>
              <Button
                colorScheme="green"
                onClick={() => props.modifiers.doSignout(true)}
              >
                Logga ut
              </Button>
              <Button colorScheme="red" onClick={() => setVerifySignout(false)}>
                Avbryt
              </Button>
            </VStack>
          </VStack>
        )}
      </>
    );
  }

  return (
    <Grid width={"90%"} templateColumns="repeat(3, 1fr)" gap={6}>
      <GridItem colSpan={3} hidden={!props.isInvoice}>
        <Text maxW={"600px"} fontStyle={"italic"}>
          Ange en epostadress som du har åtkomst till här (t.ex. din privata
          email). Emailadress för fakturering anges i nästa steg.
        </Text>
      </GridItem>
      <GridItem colSpan={3}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <MailIcon color="gray" opacity={"80%"} />
          </InputLeftElement>
          <Input
            width={"fit-content"}
            minWidth={"300px"}
            required
            type="email"
            placeholder="Din emailadress"
            disabled={session.hasUserMode(Role.USER)}
            value={props.state.email ?? ""}
            isInvalid={!isValidEmail(props.state.email)}
            onChange={(e) => {
              props.modifiers.setEmail(
                e.target.value.replaceAll(",", ".").replaceAll(" ", "")
              );
            }}
          />
        </InputGroup>
      </GridItem>
      <GridItem colSpan={3}>
        <Checkbox
          marginLeft="20px"
          isChecked={showDiscountField}
          checked={showDiscountField}
          onChange={() => {
            setShowDiscountField((old) => !old);
          }}
        >
          Rabattkod
        </Checkbox>
      </GridItem>
      <GridItem colSpan={2}>
        <Input
          hidden={!showDiscountField}
          maxLength={40}
          type="text"
          placeholder="Rabattkod"
          value={props.state.discount || ""}
          onChange={(e) => props.modifiers.setDiscount(e.target.value)}
        />
      </GridItem>
    </Grid>
  );
}

function isValidEmail(email: string | null) {
  const invalidEmail =
    email == null || email.length == 0 || !email.includes("@");
  return !invalidEmail;
}
